import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { RoleBadge, StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ShieldAlert, Plus, Upload } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  createdAt?: number;
  created_at?: number;
  organization_name?: string;
  organization_plan?: string;
  organization_id?: string;
}

function SuperAdminDashboard() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'MEMBER',
    status: 'ACTIVE',
    organizationId: '', // Optional, super admin can specify
  });

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadPackage = async () => {
    if (!uploadFile) return;
    setIsUploading(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      
      const response = await fetch(`${apiUrl}/api/admin/upload?filename=${encodeURIComponent(uploadFile.name)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: uploadFile,
      });
      
      if (response.ok) {
        alert(t('common.success'));
        setIsUploadDialogOpen(false);
        setUploadFile(null);
      } else {
        const error = await response.json();
        alert(`${t('common.error')}: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert(t('common.error'));
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users?limit=100`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.data?.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newUser),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewUser({ name: '', email: '', role: 'MEMBER', status: 'ACTIVE', organizationId: '' });
        fetchUsers();
      } else {
        const errorData = await response.json();
        alert(`${t('common.error')}: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(selectedUser),
      });

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        const errorData = await response.json();
        alert(`${t('common.error')}: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;

    setIsUploading(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/admin/upload?filename=${encodeURIComponent(uploadFile.name)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: uploadFile,
      });

      if (response.ok) {
        alert(t('common.success'));
        setUploadFile(null);
      } else {
        const errorData = await response.json();
        alert(`${t('common.error')}: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
      alert(t('common.error'));
    } finally {
      setIsUploading(false);
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedUser({ ...user });
    setIsEditDialogOpen(true);
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Platform workspace"
        icon={ShieldAlert}
        title={t('superAdmin.title')}
        subtitle={t('superAdmin.subtitle')}
        actions={(
          <>
          <Button onClick={() => setIsUploadDialogOpen(true)} variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            {t('superAdmin.uploadPackage')}
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('superAdmin.addUser')}
          </Button>
          </>
        )}
      />

      <div className="flex items-center space-x-2">
        <Label htmlFor="super-admin-user-search" className="sr-only">
          {t('superAdmin.searchPlaceholder')}
        </Label>
        <Input
          id="super-admin-user-search"
          aria-label={t('superAdmin.searchPlaceholder')}
          placeholder={t('superAdmin.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('superAdmin.uploadArtifacts.title')}</CardTitle>
          <CardDescription>
            {t('superAdmin.uploadArtifacts.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <Label htmlFor="file-upload">{t('superAdmin.uploadArtifacts.selectPackage')}</Label>
              <Input
                id="file-upload"
                type="file"
                aria-label={t('superAdmin.uploadArtifacts.selectPackage')}
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>
            <Button
              onClick={handleFileUpload}
              disabled={!uploadFile || isUploading}
              className="mt-auto"
            >
              {isUploading ? (
                <>{t('superAdmin.uploadArtifacts.uploading')}</>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('superAdmin.uploadArtifacts.upload')}
                </>
              )}
            </Button>
          </div>
          {uploadFile && (
            <div className="mt-2 text-sm text-muted-foreground flex items-center">
              {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('superAdmin.usersTable.title')} ({filteredUsers.length})</CardTitle>
          <CardDescription>
            {t('superAdmin.usersTable.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.userName')}</TableHead>
                <TableHead>{t('users.email')}</TableHead>
                <TableHead>{t('navigation.organizations')}</TableHead>
                <TableHead>{t('billing.plan')}</TableHead>
                <TableHead>{t('users.role')}</TableHead>
                <TableHead>{t('users.status')}</TableHead>
                <TableHead>{t('licenses.createdAt')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell className="font-medium">
                      {user.organization_name || 'N/A'}
                      {user.organization_id && <span className="text-xs text-muted-foreground block truncate max-w-[100px]">{user.organization_id}</span>}
                    </TableCell>
                    <TableCell>
                      {user.organization_plan ? (
                        <Badge variant="outline">{user.organization_plan}</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={user.status} />
                    </TableCell>
                    <TableCell>
                      {formatDate(user.created_at || user.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)}>
                        {t('common.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('superAdmin.dialogs.addUser.title')}</DialogTitle>
            <DialogDescription>
              {t('superAdmin.dialogs.addUser.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('users.userName')}</Label>
              <Input
                id="name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t('users.email')}</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="orgId">{t('superAdmin.dialogs.addUser.orgId')}</Label>
              <Input
                id="orgId"
                value={newUser.organizationId}
                onChange={(e) => setNewUser({ ...newUser,organizationId: e.target.value })}
                placeholder={t('superAdmin.dialogs.addUser.placeholderOrgId')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">{t('users.role')}</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  <SelectItem value="OWNER">Owner</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="MEMBER">Member</SelectItem>
                  <SelectItem value="VIEWER">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">{t('users.status')}</Label>
              <Select
                value={newUser.status}
                onValueChange={(value) => setNewUser({ ...newUser, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">{t('licenses.active')}</SelectItem>
                  <SelectItem value="INACTIVE">{t('licenses.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateUser} disabled={!newUser.name || !newUser.email}>
              {t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('superAdmin.dialogs.editUser.title')}</DialogTitle>
            <DialogDescription>
              {t('superAdmin.dialogs.editUser.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">{t('users.userName')}</Label>
                <Input
                  id="edit-name"
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-email">{t('users.email')}</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={selectedUser.email}
                  onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-role">{t('users.role')}</Label>
                <Select
                  value={selectedUser.role}
                  onValueChange={(value) => setSelectedUser({ ...selectedUser, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                    <SelectItem value="OWNER">Owner</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="MEMBER">Member</SelectItem>
                    <SelectItem value="VIEWER">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-status">{t('users.status')}</Label>
                <Select
                  value={selectedUser.status}
                  onValueChange={(value) => setSelectedUser({ ...selectedUser, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t('licenses.active')}</SelectItem>
                    <SelectItem value="INACTIVE">{t('licenses.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditUser}>
              {t('common.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Package Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('superAdmin.dialogs.uploadPackage.title')}</DialogTitle>
            <DialogDescription>
              {t('superAdmin.dialogs.uploadPackage.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="file">{t('superAdmin.uploadArtifacts.selectPackage')}</Label>
              <Input
                id="file"
                type="file"
                accept=".deb,.rpm"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUploadPackage} disabled={!uploadFile || isUploading}>
              {isUploading ? t('superAdmin.uploadArtifacts.uploading') : t('superAdmin.uploadArtifacts.upload')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SuperAdminDashboard;
